/**
 * SlideOutPanel — Reusable slide-out panel component
 *
 * A flexible panel that slides in from the right (or left) side of the screen.
 * Features:
 * - Smooth animations
 * - Backdrop overlay
 * - Responsive design (full-screen on mobile, percentage-width on desktop)
 * - Accessibility (ARIA labels, keyboard support)
 * - Non-blocking (users can dismiss by clicking backdrop or X button)
 */

import { ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface SlideOutPanelProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  position?: 'right' | 'left';
  width?: 'narrow' | 'medium' | 'wide'; // narrow: 400px, medium: 500px, wide: 600px
  closeOnBackdropClick?: boolean;
  /** Optional actions rendered in the header bar, to the left of the close button */
  headerActions?: ReactNode;
}

export function SlideOutPanel({
  isOpen,
  onClose,
  title,
  children,
  position = 'right',
  width = 'medium',
  closeOnBackdropClick = true,
  headerActions,
}: SlideOutPanelProps) {
  const widthMap = {
    narrow: 'w-96',
    medium: 'w-full sm:w-[500px]',
    wide: 'w-full sm:w-[600px]',
  };

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className={cn(
          'fixed inset-0 bg-black/30 z-30 transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={closeOnBackdropClick ? onClose : undefined}
        role="presentation"
        aria-hidden={!isOpen}
      />

      {/* Slide-out panel */}
      <div
        className={cn(
          'fixed top-0 bottom-0 z-40 bg-white dark:bg-metallic-900 shadow-2xl',
          'transition-all duration-300 ease-in-out overflow-hidden',
          'flex flex-col',
          widthMap[width],
          position === 'right' ? 'right-0' : 'left-0',
          isOpen
            ? position === 'right'
              ? 'translate-x-0'
              : 'translate-x-0'
            : position === 'right'
              ? 'translate-x-full'
              : '-translate-x-full'
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="slide-out-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b dark:border-metallic-800 flex-shrink-0">
          <h2
            id="slide-out-title"
            className="font-semibold text-lg text-gray-900 dark:text-white"
          >
            {title}
          </h2>
          <div className="flex items-center gap-1">
            {headerActions}
            <button
              onClick={onClose}
              className={cn(
                'p-1.5 rounded-lg transition-colors',
                'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200',
                'hover:bg-gray-100 dark:hover:bg-metallic-800'
              )}
              aria-label="Close panel"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {children}
        </div>
      </div>
    </>
  );
}

export default SlideOutPanel;

// Made with Bob
